import { Box, CircularProgress, Fade, Modal, useMediaQuery } from '@material-ui/core';
import { SelectableProfile } from '@nemuru-eng/js-api-client';

import { Font } from '@/components/shared/components/Font';
import { ProfileAvatar } from '@/components/shared/components/ProfileAvatar';
import { Text } from '@/components/shared/components/Text';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

const ENTER_TIMEOUT_MS = 200;
const EXIT_TIMEOUT_MS = 300;

interface ProfileSwitchOverlayProps {
  profile?: SelectableProfile;
  open: boolean;
}

/** Full-screen cover shown from the click on a profile until the page is replaced. */
export const ProfileSwitchOverlay = ({ profile, open }: ProfileSwitchOverlayProps) => {
  const { palette } = useCustomTheme();
  const {
    state: { appTheme },
  } = useConfigContext();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  if (!profile) return null;

  const profileName = profile.displayName ?? profile.username;
  const timeout = prefersReducedMotion ? 0 : { enter: ENTER_TIMEOUT_MS, exit: EXIT_TIMEOUT_MS };

  return (
    <Modal open={open} closeAfterTransition disableEscapeKeyDown BackdropProps={{ invisible: true }}>
      <Fade in={open} timeout={timeout}>
        <Box
          role="status"
          aria-live="polite"
          aria-busy="true"
          tabIndex={-1}
          position="fixed"
          top={0}
          left={0}
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gridGap={24}
          bgcolor={appTheme?.background ?? 'white'}
          color={palette.text.primary}
          style={{ outline: 'none' }}
        >
          <ProfileAvatar profile={profile} />
          <Font variant="subtitle1" color="inherit">
            <Text tId="PROFILE_SWITCHING_TO" dynamicDataTexts={[profileName]} />
          </Font>
          <CircularProgress color="inherit" thickness={4} size={28} />
        </Box>
      </Fade>
    </Modal>
  );
};
